import type { AssistantPermissions, Assistant } from '@/types/entities';

/**
 * Permission helper utilities for assistant access control
 * Now uses Supabase Auth instead of custom session tokens
 */

// Storage key for restaurant redirect
const ASSISTANT_RESTAURANT_KEY = 'digimataam_assistant_restaurant';

/**
 * Check if an assistant is currently logged in
 * Uses Supabase Auth session
 * Note: This is a synchronous approximation. For accurate auth state, use useAssistantAuth() hook
 */
export function isAssistantLoggedIn(): boolean {
  // Try to get session synchronously from local storage
  // Supabase stores session in localStorage under specific keys
  try {
    const authKey = Object.keys(localStorage).find(key =>
      key.startsWith('sb-') && key.includes('-auth-token')
    );
    if (!authKey) return false;

    const authData = localStorage.getItem(authKey);
    if (!authData) return false;

    const parsed = JSON.parse(authData);
    const userType = parsed?.user?.user_metadata?.user_type;
    return userType === 'assistant';
  } catch {
    return false;
  }
}

/**
 * @deprecated No longer needed with Supabase Auth. Use useAssistantAuth() hook instead.
 * Get the current assistant session token
 */
export function getAssistantSessionToken(): string | null {
  return null; // Session tokens no longer used
}

/**
 * @deprecated Use useAssistantAuth() hook instead for assistant data
 * Get the current assistant's data
 */
export function getAssistantData(): Assistant | null {
  return null; // Use useAssistantAuth() hook instead
}

/**
 * Get the current assistant's restaurant ID from localStorage
 * This is still used for redirect purposes
 */
export function getAssistantRestaurantId(): string | null {
  return localStorage.getItem(ASSISTANT_RESTAURANT_KEY);
}

// Default empty permissions (all false)
export const EMPTY_PERMISSIONS: AssistantPermissions = {
  dashboard: { view: false },
  cancellations: { view: false, accept_reject: false },
  menu: { view: false, add: false, edit: false, delete: false },
  stock: {
    view: false,
    add_receipt: false,
    add_validation: false,
    edit_receipt: false,
    delete_receipt: false,
    delete_validation: false,
  },
  staff: { view: false, add: false, edit: false, delete: false },
  salaries: { view: false, add: false, edit: false, delete: false },
  expenses: { view: false, add: false, edit: false, delete: false },
};

// Predefined role templates with default permissions
export const ROLE_TEMPLATES = {
  manager: {
    nameAr: 'مدير',
    nameFr: 'Manager',
    descriptionAr: 'صلاحيات إدارية كاملة تقريباً',
    descriptionFr: 'Nearly full administrative permissions',
    permissions: {
      dashboard: { view: true },
      cancellations: { view: true, accept_reject: true },
      menu: { view: true, add: true, edit: true, delete: true },
      stock: {
        view: true,
        add_receipt: true,
        add_validation: true,
        edit_receipt: true,
        delete_receipt: false,
        delete_validation: false,
      },
      staff: { view: true, add: true, edit: true, delete: true },
      salaries: { view: true, add: true, edit: true, delete: true },
      expenses: { view: true, add: true, edit: true, delete: true },
    } as AssistantPermissions,
  },
  receptionist: {
    nameAr: 'موظف استقبال',
    nameFr: 'Réceptionniste',
    descriptionAr: 'صلاحيات محدودة للاستقبال',
    descriptionFr: 'Limited permissions for reception',
    permissions: {
      dashboard: { view: true },
      cancellations: { view: true, accept_reject: false },
      menu: { view: true, add: false, edit: false, delete: false },
      stock: {
        view: false,
        add_receipt: false,
        add_validation: false,
        edit_receipt: false,
        delete_receipt: false,
        delete_validation: false,
      },
      staff: { view: false, add: false, edit: false, delete: false },
      salaries: { view: false, add: false, edit: false, delete: false },
      expenses: { view: false, add: false, edit: false, delete: false },
    } as AssistantPermissions,
  },
  inventory_manager: {
    nameAr: 'مدير المخزون',
    nameFr: 'Gestionnaire d\'Inventaire',
    descriptionAr: 'صلاحيات إدارة المخزون والقائمة',
    descriptionFr: 'Inventory and menu management permissions',
    permissions: {
      dashboard: { view: true },
      cancellations: { view: false, accept_reject: false },
      menu: { view: true, add: true, edit: true, delete: false },
      stock: {
        view: true,
        add_receipt: true,
        add_validation: true,
        edit_receipt: true,
        delete_receipt: true,
        delete_validation: false,
      },
      staff: { view: false, add: false, edit: false, delete: false },
      salaries: { view: false, add: false, edit: false, delete: false },
      expenses: { view: false, add: false, edit: false, delete: false },
    } as AssistantPermissions,
  },
};

/**
 * Check if assistant has specific permission
 * @param permissions - Assistant's permissions object
 * @param page - Page/module name (e.g., 'menu', 'stock')
 * @param action - Action name (e.g., 'view', 'add', 'edit')
 * @returns true if permission is granted
 */
export function hasPermission(
  permissions: AssistantPermissions | undefined,
  page: keyof AssistantPermissions,
  action: string
): boolean {
  if (!permissions || !permissions[page]) return false;

  const pagePermissions = permissions[page] as Record<string, boolean>;
  return pagePermissions[action] === true;
}

/**
 * Check if assistant can view a page (has at least one permission)
 * @param permissions - Assistant's permissions object
 * @param page - Page/module name
 * @returns true if at least one permission is granted for the page
 */
export function canViewPage(
  permissions: AssistantPermissions | undefined,
  page: keyof AssistantPermissions
): boolean {
  if (!permissions || !permissions[page]) return false;

  const pagePermissions = permissions[page] as Record<string, boolean>;
  return Object.values(pagePermissions).some((val) => val === true);
}

/**
 * Merge permissions with defaults (ensure all keys exist)
 * @param permissions - Partial permissions from database
 * @returns Complete permissions object with defaults
 */
export function mergePermissions(
  permissions: Partial<AssistantPermissions>
): AssistantPermissions {
  return {
    dashboard: { ...EMPTY_PERMISSIONS.dashboard, ...permissions.dashboard },
    cancellations: {
      ...EMPTY_PERMISSIONS.cancellations,
      ...permissions.cancellations,
    },
    menu: { ...EMPTY_PERMISSIONS.menu, ...permissions.menu },
    stock: { ...EMPTY_PERMISSIONS.stock, ...permissions.stock },
    staff: { ...EMPTY_PERMISSIONS.staff, ...permissions.staff },
    salaries: { ...EMPTY_PERMISSIONS.salaries, ...permissions.salaries },
    expenses: { ...EMPTY_PERMISSIONS.expenses, ...permissions.expenses },
  };
}

/**
 * Get all permission keys for a specific page
 * @param page - Page/module name
 * @returns Array of action keys for that page
 */
export function getPageActions(page: keyof AssistantPermissions): string[] {
  const pagePerms = EMPTY_PERMISSIONS[page] as Record<string, boolean>;
  return Object.keys(pagePerms);
}
